import { Field, Text } from "@sitecore-jss/sitecore-jss-react";
import React, { useMemo } from "react";

import { salutations, useOfName } from "../../../Enums/enumValues";
import { Enum, UseOfName } from "../../../interfaces/enum";
import { ModelState } from "../../../interfaces/modelState";
import { getFullName } from "../../../lib/nameUtil";
import { PersonalDetailsModel } from "../../../providers/dupFlow/DupFlow.interfaces";
import { Card } from "../../card/Card";
import DateInput from "../../InputFields/DateInput";
import HorizontalRadioInput from "../../InputFields/HorizontalRadioInput";
import { capitalize, V23, V26, V27, V48, V58 } from "../../InputFields/sanitizer/inputSanitizers";
import { SelectionListInput, SelectOption } from "../../InputFields/SelectionListInput";
import SwitchInput from "../../InputFields/SwitchInput";
import TextInput from "../../InputFields/TextInput";
import { V5, V10, V11, V12, V24 } from "../../InputFields/validator/validators";
import { AddressFields } from "./AddressFields";

interface Props {
  data: Partial<PersonalDetailsModel>;
  index: number;
  fields: Record<string, Field<string>>;
  modelState: ModelState | null;
  setModelState: (updatedModelState: ModelState) => void;
  onUpdateInsured: (index: number, data: Partial<PersonalDetailsModel>) => void;
  handleEditDateOfBirth: () => void;
  hideAddressAndContactDetails?: boolean;
  isSecondInsured?: boolean;
}

export const InsuredPersonDetails = ({
  index,
  fields,
  data,
  onUpdateInsured,
  setModelState,
  isSecondInsured,
  modelState,
  handleEditDateOfBirth,
  hideAddressAndContactDetails,
}: Props) => {
  const salutationOptions = salutations() as Enum[];
  const useOfNameOptions = (useOfName() as Enum[])
    .filter((useOfNameOption) => useOfNameOption.value !== "OwnName")
    .map((useOfNameOption) => {
      return {
        key: useOfNameOption.value,
        value: useOfNameOption.label,
      };
    }) as SelectOption<UseOfName>[];

  const handleInsuredSalutation = (salutation: string) => {
    onUpdateInsured(index, { salutation });
  };

  const handleInsuredInitials = (initials: string) => {
    onUpdateInsured(index, { initials });
  };

  const handleInsuredFirstName = (firstName: string) => {
    onUpdateInsured(index, { firstName });
  };

  const handleInsuredInserts = (inserts: string) => {
    onUpdateInsured(index, { inserts });
  };

  const handleInsuredLastName = (lastName: string) => {
    onUpdateInsured(index, { lastName });
  };

  const handleInsuredHasPartner = (hasPartner: boolean) => {
    if (hasPartner) {
      onUpdateInsured(index, { hasPartner });
    } else {
      onUpdateInsured(index, {
        hasPartner,
        partnerInserts: undefined,
        partnerLastName: undefined,
        useOfName: "PartnerName",
      });
    }
  };

  const handleInsuredPartnerInserts = (partnerInserts: string) => {
    onUpdateInsured(index, { partnerInserts });
  };

  const handleInsuredPartnerLastName = (partnerLastName: string) => {
    onUpdateInsured(index, { partnerLastName });
  };

  const handleInsuredUseOfName = (value: UseOfName) => {
    onUpdateInsured(index, { useOfName: value });
  };

  const handleInsuredEmailAddress = (emailAddress: string) => {
    onUpdateInsured(index, { emailAddress });
  };

  const handleInsuredMobileNumber = (mobileNumber: string) => {
    onUpdateInsured(index, { mobileNumber });
  };

  const handleInsuredHasTelephoneNumber = (hasTelephoneNumber: boolean) => {
    if (hasTelephoneNumber) {
      onUpdateInsured(index, { hasTelephoneNumber });
    } else {
      onUpdateInsured(index, { hasTelephoneNumber, telephoneNumber: undefined });
    }
  };

  const handleInsuredTelephoneNumber = (telephoneNumber: string) => {
    onUpdateInsured(index, { telephoneNumber });
  };

  const handleInsuredHasDifferentAddress = (hasDifferentAddress: boolean) => {
    onUpdateInsured(index, { hasDifferentAddress });
  };

  const fullName = useMemo(() => getFullName(data), [data]);

  const insuredType = `InsuredPersons[${index}]`;

  const title = () => {
    if (data.isChild) {
      return fields.TitleChild;
    } else if (isSecondInsured) {
      return fields.TitleSecondInsured;
    } else {
      return fields.TitleFirstInsured;
    }
  };

  const showAddress = !isSecondInsured || data.hasDifferentAddress;

  const renderAddressDetails = () => (
    <>
      <div className="form-row-divider"></div>
      <h2>
        <Text field={fields.TitleAddressDetails} />
      </h2>
      {isSecondInsured && (
        <SwitchInput
          fieldData={fields}
          value={data.hasDifferentAddress}
          name="HasDifferentAddress"
          fieldName={`HasDifferentAddress${insuredType}`}
          onChange={handleInsuredHasDifferentAddress}
        />
      )}
      {showAddress && (
        <AddressFields
          fields={fields}
          data={data}
          modelState={modelState}
          setModelState={setModelState}
          partialFieldName={insuredType}
          onUpdateAddress={(partialAddress) => onUpdateInsured(index, partialAddress)}
        />
      )}
      <div className="form-row-divider"></div>
      <h2>
        <Text field={fields.TitleContactDetails} />
      </h2>
      <TextInput
        fieldData={fields}
        name="EmailAddress"
        validators={[V5, V10(99), V12]}
        setModelState={setModelState}
        inputValue={data.emailAddress}
        modelState={modelState}
        fieldName={`EmailAddress${insuredType}`}
        propertyName={`model.${insuredType}.EmailAddress`}
        onBlur={handleInsuredEmailAddress}
        required
      />
      <TextInput
        fieldData={fields}
        name="MobileNumber"
        validators={[V5, V10(15), V24]}
        setModelState={setModelState}
        inputValue={data.mobileNumber}
        modelState={modelState}
        fieldName={`MobileNumber${insuredType}`}
        propertyName={`model.${insuredType}.MobileNumber`}
        onBlur={handleInsuredMobileNumber}
        required
      />
      <SwitchInput
        fieldData={fields}
        value={data.hasTelephoneNumber}
        name="HasTelephoneNumber"
        fieldName={`HasTelephoneNumber${insuredType}`}
        onChange={handleInsuredHasTelephoneNumber}
      />
      {data.hasTelephoneNumber && (
        <TextInput
          fieldData={fields}
          name="TelephoneNumber"
          validators={[V5, V10(15), V24]}
          setModelState={setModelState}
          inputValue={data.telephoneNumber}
          modelState={modelState}
          fieldName={`TelephoneNumber${insuredType}`}
          propertyName={`model.${insuredType}.TelephoneNumber`}
          onBlur={handleInsuredTelephoneNumber}
          required
        />
      )}
    </>
  );

  return (
    <Card>
      <h2>
        <Text field={title()} />
      </h2>
      <HorizontalRadioInput
        fieldData={fields}
        name="Salutation"
        setModelState={setModelState}
        selectedValue={data.salutation}
        options={salutationOptions}
        modelState={modelState}
        fieldName={`Salutation${insuredType}`}
        propertyName={`model.${insuredType}.Salutation`}
        onChange={handleInsuredSalutation}
      />
      <div className="form-row form-row--flex">
        <TextInput
          fieldData={fields}
          name="Initials"
          sanitize={[V23, V48, capitalize]}
          validators={[V5, V10(7)]}
          setModelState={setModelState}
          inputValue={data.initials}
          modelState={modelState}
          onBlur={handleInsuredInitials}
          fieldName={`Initials${insuredType}`}
          propertyName={`model.${insuredType}.Initials`}
          widthClassName="short"
          excludeRow={true}
          required
        />
        <TextInput
          fieldData={fields}
          name="FirstName"
          sanitize={[V27, V48, V58]}
          validators={[V5, V10(35), V11(2)]}
          setModelState={setModelState}
          inputValue={data.firstName}
          modelState={modelState}
          onBlur={handleInsuredFirstName}
          fieldName={`FirstName${insuredType}`}
          propertyName={`model.${insuredType}.FirstName`}
          widthClassName="medium"
          excludeRow={true}
          required
        />
      </div>
      <div className="form-row form-row--flex">
        <TextInput
          fieldData={fields}
          name="Inserts"
          sanitize={[V48]}
          validators={[V10(10)]}
          setModelState={setModelState}
          inputValue={data.inserts}
          modelState={modelState}
          onBlur={handleInsuredInserts}
          fieldName={`Inserts${insuredType}`}
          propertyName={`model.${insuredType}.Inserts`}
          widthClassName="short"
          excludeRow={true}
        />
        <TextInput
          fieldData={fields}
          name="Lastname"
          sanitize={[V26, V48, V58]}
          validators={[V5, V10(35)]}
          setModelState={setModelState}
          inputValue={data.lastName}
          modelState={modelState}
          onBlur={handleInsuredLastName}
          fieldName={`Lastname${insuredType}`}
          propertyName={`model.${insuredType}.LastName`}
          widthClassName="medium"
          excludeRow={true}
          required
        />
      </div>
      {!data.isChild && (
        <>
          <SwitchInput
            fieldData={fields}
            value={data.hasPartner}
            name="HasPartner"
            fieldName={`HasPartner${insuredType}`}
            onChange={handleInsuredHasPartner}
          />
          {data.hasPartner && (
            <>
              <div className="form-row form-row--flex">
                <TextInput
                  fieldData={fields}
                  name="PartnerInserts"
                  sanitize={[V48]}
                  validators={[V10(10)]}
                  setModelState={setModelState}
                  inputValue={data.partnerInserts}
                  modelState={modelState}
                  fieldName={`PartnerInserts${insuredType}`}
                  propertyName={`model.${insuredType}.PartnerInserts`}
                  onBlur={handleInsuredPartnerInserts}
                  widthClassName="short"
                  excludeRow={true}
                />
                <TextInput
                  fieldData={fields}
                  name="PartnerLastname"
                  sanitize={[V26, V48, V58]}
                  validators={[V5, V10(35)]}
                  setModelState={setModelState}
                  inputValue={data.partnerLastName}
                  modelState={modelState}
                  fieldName={`PartnerLastname${insuredType}`}
                  propertyName={`model.${insuredType}.PartnerLastName`}
                  onBlur={handleInsuredPartnerLastName}
                  widthClassName="medium"
                  excludeRow={true}
                />
              </div>
              <SelectionListInput
                id={`partner_selection_id_${index}`}
                fieldData={fields}
                options={useOfNameOptions}
                value={data.useOfName}
                name="UseOfName"
                propertyName={`model.${insuredType}.UseOfName`}
                fieldName={`UseOfName${insuredType}`}
                onChange={handleInsuredUseOfName}
                hideDefaultSelection={true}
              />
            </>
          )}
          <div className="form-row">
            <Text field={fields.HeadingFullName} />:
            <br />
            <strong>{fullName}</strong>
          </div>
        </>
      )}
      <DateInput
        fieldData={fields}
        name="DateOfBirth"
        setModelState={setModelState}
        inputValue={data.dateOfBirth}
        modelState={modelState}
        fieldName={`DateOfBirth${insuredType}`}
        propertyName={`model.${insuredType}.DateOfBirth`}
        disabled={true}
        onEditClick={handleEditDateOfBirth}
      />
      {!hideAddressAndContactDetails && renderAddressDetails()}
    </Card>
  );
};
